import React, { useState } from 'react';

import { UiButton } from '../ui/ui-button';

import { confirm } from '../widgets/confirmation-dialog';

type InputProps = {
  images: Array<ImageGalleryImage>;

  children?: any;
  imageDisplayLimit?: number;
  minimumSize?: number;
  onClick?: (image: ImageGalleryImage) => void;
  onDelete?: (image: ImageGalleryImage) => void;
};

export const ImageGallery = ({
  images,
  imageDisplayLimit,
  onClick,
  onDelete,
  minimumSize,
  children,
}: InputProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [filteredImages, setFilteredImages] = useState<{ [url: string]: boolean }>({});

  const handleShowMoreClick = () => setIsExpanded(!isExpanded);
  const handleImageClick = (image: ImageGalleryImage) =>
    onClick ? () => onClick(image) : undefined;
  const handleImageDeleteClick = (image: ImageGalleryImage) =>
    onDelete
      ? async () => {
          if (await confirm('Are you sure you want to delete this image?')) {
            onDelete(image);
          }
        }
      : undefined;
  const onImageError = (imageUrl: string) => () => {
    setFilteredImages({
      ...filteredImages,
      [imageUrl]: true,
    });
  };
  const checkImage = (imageUrl: string) =>
    minimumSize
      ? (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
          const imageElement = event.target as HTMLImageElement;
          if (imageElement.naturalHeight < minimumSize && imageElement.naturalWidth < minimumSize) {
            setFilteredImages({
              ...filteredImages,
              [imageUrl]: true,
            });
          }
        }
      : undefined;

  const correctlySizedImages = images.filter((image) => !filteredImages[image.imageUrl]);
  const displayedImages = isExpanded
    ? correctlySizedImages
    : correctlySizedImages.slice(0, imageDisplayLimit);
  const lessImagesThanLimit =
    (imageDisplayLimit && images.length <= imageDisplayLimit) || !imageDisplayLimit;
  const displayShowMoreButton = imageDisplayLimit && imageDisplayLimit > 0 && !lessImagesThanLimit;

  const deleteButtonStyles = {
    position: 'absolute',
    top: '0px',
    right: '0px',
  };

  return (
    <div className="u-display-flex u-flexWrap-wrap u-margin-top-lg">
      {displayedImages.map((di) => (
        <div
          key={di.id}
          role="presentation"
          className={`u-width-33 u-padding-sm u-position-relative ${
            onClick ? 'u-backgroundHover-lightGray' : ''
          }`}
        >
          <img
            role="presentation"
            src={di.imageUrl}
            alt={di.alt}
            className={`u-width-100 ${onClick ? 'u-cursor-pointer' : ''}`}
            onError={onImageError(di.imageUrl)}
            onClick={handleImageClick(di)}
            onLoad={checkImage(di.imageUrl)}
          />
          {onDelete ? (
            <UiButton
              size="xxs"
              style={deleteButtonStyles}
              className="imageDelete-button u-background-red"
              onClick={handleImageDeleteClick(di)}
            >
              X
            </UiButton>
          ) : null}
        </div>
      ))}
      {displayShowMoreButton ? (
        <div className="u-width-33 u-minHeight-150px u-padding-sm u-display-flex u-flexDirection-column u-alignItems-center u-justifyContent-center">
          <UiButton size="sm" className="u-margin-right" onClick={handleShowMoreClick}>
            Show {isExpanded ? 'Less' : 'More'}...
          </UiButton>
          {(displayShowMoreButton && isExpanded) || lessImagesThanLimit ? children : null}
        </div>
      ) : null}
    </div>
  );
};
